// recova GmbH – Kamera-Barcode-Scanner (Code128/EAN-13/QR) als Dialog-Komponente.
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CameraOff } from "lucide-react";

const SCAN_REGION_ID = "barcode-scan-region";

export const BarcodeScanner = ({ open, onClose, onScan, title = "Barcode scannen" }) => {
  const [error, setError] = useState(null);
  const active = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    let scanner = null;
    let done = false;
    const t = setTimeout(() => {
      const el = document.getElementById(SCAN_REGION_ID);
      if (!el) {
        setError("Scanner konnte nicht initialisiert werden – bitte erneut versuchen.");
        return;
      }
      try {
        scanner = new Html5Qrcode(SCAN_REGION_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          verbose: false,
        });
        active.current = scanner;
      } catch (e) {
        setError("Scanner konnte nicht initialisiert werden – bitte erneut versuchen.");
        return;
      }
      scanner
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 130 } },
          (text) => {
            if (done) return;
            done = true;
            onScan(text);
          },
          () => {}
        )
        .catch(() => setError("Kamera nicht verfügbar – bitte Kamerazugriff erlauben und erneut versuchen."));
    }, 150);
    return () => {
      clearTimeout(t);
      done = true;
      const s = active.current;
      active.current = null;
      if (s) {
        try {
          if (s.isScanning) s.stop().then(() => s.clear()).catch(() => {});
          else s.clear();
        } catch (e) {}
      }
    };
  }, [open, onScan]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">Kamerabild zum Scannen eines Barcodes</DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-red-50 p-6 text-center text-sm text-red-700" data-testid="scanner-error">
            <CameraOff className="h-6 w-6" />
            {error}
          </div>
        ) : (
          <>
            <div id={SCAN_REGION_ID} className="overflow-hidden rounded-xl" data-testid="scanner-viewport" />
            <p className="text-center text-xs text-slate-400">Etikett mit dem Barcode vor die Kamera halten…</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
